/**
 * The lightbox as a modal dialog (concepts/frontend-design.md, "The lightbox"): while it is
 * open it carries aria-modal and a name, it takes the focus once it has opened, the rest of
 * the page is inert - Tab stays in the lightbox and a screen reader reads only it -, and when
 * it closes the focus goes back to what opened it. Until 7.0.0 the focus stayed on the
 * thumbnail behind the lightbox, Tab walked out into the page under the black backdrop, and
 * the dialog had no name (A11Y-2).
 *
 * PhotoSwipe's own focus trap stays off (trapFocus: false in PhotoSwipeGallery.js): it pulls
 * the focus back into the lightbox on every focusin outside it, also out of the share and the
 * format dialog, which Overlay.js opens on the body of the page; that broke the format dialog
 * once (#1240). The page behind is made inert instead - the children of the body which are
 * there when the lightbox opens - and a dialog the overlay adds later is a new child, which
 * its showModal() lays over everything, the lightbox too.
 */
export default class LightboxModality {

    /**
     * @param {import('photoswipe').default} pswp the lightbox, before its init()
     * @param {object} [options]
     * @param {string} [options.label] the name of the lightbox (KEY_LIGHTBOX_LABEL); none in the backend
     * @param {Element|null} [options.opener] what gets the focus back; the focused element by default
     */
    static bind(pswp, options = {}) {
        return new LightboxModality(pswp, options);
    }

    constructor(pswp, {label = '', opener = (typeof document !== 'undefined' ? document.activeElement : null)} = {}) {
        this.pswp = pswp;
        this.label = label;
        this.opener = opener;
        /** @type {Element[]} the elements this instance made inert */
        this.inerted = [];

        pswp.on('afterInit', () => this.open());
        // the focus moves in once the opening animation is over: focus causes a layout,
        // which would make the animation stutter (PhotoSwipe waits for the same reason)
        pswp.on('openingAnimationEnd', () => this.focus());
        pswp.on('close', () => this.release());
        pswp.on('destroy', () => this.returnFocus());
    }

    /**
     * The name and the modal semantics of the lightbox, and the page behind it inert.
     */
    open() {
        const element = this.pswp.element;
        if (!element) {
            return;
        }
        element.setAttribute('aria-modal', 'true');
        if (this.label) {
            element.setAttribute('aria-label', this.label);
        }

        const parent = element.parentElement;
        if (!parent) {
            return;
        }
        for (const child of Array.from(parent.children)) {
            if (child === element || child.hasAttribute('inert') || child.tagName === 'SCRIPT' || child.tagName === 'DIALOG') {
                continue;
            }
            child.setAttribute('inert', '');
            this.inerted.push(child);
        }
    }

    /**
     * The focus into the lightbox, unless it is already there.
     */
    focus() {
        const element = this.pswp.element;
        if (element && !element.contains(document.activeElement)) {
            element.focus({preventScroll: true});
        }
    }

    /**
     * The page behind becomes usable again, when the lightbox starts to close.
     */
    release() {
        for (const child of this.inerted) {
            child.removeAttribute('inert');
        }
        this.inerted = [];
    }

    /**
     * The focus back to what opened the lightbox, if it is still on the page.
     */
    returnFocus() {
        this.release();
        const opener = this.opener;
        if (opener && opener !== document.body && opener.isConnected && typeof opener.focus === 'function') {
            opener.focus({preventScroll: true});
        }
    }
}
